import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  nombre: string;
  hogarId: string;
  rol: 'OWNER' | 'ADMIN' | 'MIEMBRO';
}

interface AuthState {
  user: User | null;
  /**
   * accessToken: in-memory ONLY. Never persisted to localStorage / Preferences.
   * Lifetime ~15min; re-derived via refresh on cold start.
   */
  accessToken: string | null;
  /**
   * refreshToken: in-memory cache of the value loaded from secureStorage at
   * boot. The canonical store is secureStorage (Keychain on iOS,
   * EncryptedSharedPreferences on Android, localStorage on web fallback).
   * NOT persisted via Zustand persist middleware (see partialize below).
   */
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * C16: gates components that depend on the bootstrap auth refresh having
   * completed. Set to `true` from `mobileBootstrap()` AFTER `bootstrapAuth()`
   * has either populated the access token from a successful /auth/refresh or
   * given up and cleared the session. Components like DashboardPage check
   * this flag before firing requests to avoid the 401-stampede on cold
   * start. NOT a Zustand-persist hydration flag — that callback used to flip
   * this on rehydrate, but Option C re-purposes the flag to mean "boot
   * finished" so that consumers do not need a second piece of state.
   */
  _hasHydrated: boolean;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      setAccessToken: (accessToken) => set({ accessToken }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),

      setLoading: (isLoading) => set({ isLoading }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setHasHydrated: (_hasHydrated) => set({ _hasHydrated }),
    }),
    {
      name: 'solid-cycle-auth',
      // C15/C16: persist ONLY the user profile + isAuthenticated flag in
      // localStorage. accessToken stays in memory; refreshToken lives in
      // secureStorage (Keychain/EncryptedSharedPreferences on native,
      // localStorage fallback on web). Bootstrap (src/lib/mobileBootstrap.ts
      // + src/services/api.ts:bootstrapAuth) does a refresh-first round trip
      // BEFORE React mounts so accessToken is in memory by first render.
      // We deliberately do NOT persist accessToken — Option C of the C16
      // T1-A handoff confirmed XSS surface stays at "in-memory only".
      partialize: (state) => ({
        user: state.user,
        // isAuthenticated is derived from user presence + token bootstrap, so we
        // also keep it persisted to avoid a flash of /login at cold start while
        // the refresh request is in flight.
        isAuthenticated: state.isAuthenticated,
      }),
      // C16: do NOT auto-flip _hasHydrated from the Zustand rehydrate hook.
      // That flag now tracks "mobileBootstrap finished" (i.e. the refresh-first
      // bootstrap round trip is settled, success or fail) so components that
      // gate on auth state can wait for the canonical signal. The flip lives
      // in mobileBootstrap.ts to keep the precondition single-sourced.
    }
  )
);
